import React, { useCallback, useMemo, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useField, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import DropzoneField from 'components/DropzoneField';
import validations from 'validations';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import { User } from 'models/User';
import { Phone } from 'phoneNumberUtils';
import api from 'api';
import InvestorVerify from './components/InvestorVerify';
import CurrentProfileContext from '../../providers/CurrentProfile/CurrentProfile.context';
import Company from '../Company';
import ProfileActions from '../../components/ProfileActions';
import ProfileActionsWithVerification from './components/ProfileActionsWithVerification';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import EmailField from 'components/EmailField';
import PhoneVerificationModal from 'components/PhoneVerificationModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import TemporaryNotifications from '../TemporaryNotifications';
import useMatchMedia from 'hooks/useMatchMedia';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import { NAME_MAX_LENGTH } from 'data/global';

import './ProfilePage.styles.scss';
import './ProfilePage.styles.responsive.scss';

type ProfilePageProps = {
  className?: string;
};

const ProfilePage: React.FC<ProfilePageProps> = (props) => {
  const { className } = props;

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { currentProfile, updateProfilePicture } = useContext(
    CurrentProfileContext,
  );

  const [phoneVerificationModal, phoneVerificationModalRef] =
    useCallbackRef<ModalRef>();

  const classes = classNames('profile-page', className);

  const {
    values: { phone, hasVerifiedContactNumber },
  } = useFormState<User>();

  const {
    meta: { dirty: contactChanged, error: contactError },
  } = useField<Phone>('phone');

  const {
    meta: { dirty: emailChanged },
  } = useField('email');

  const verifiedPhone = useMemo(
    () => hasVerifiedContactNumber && !contactChanged,
    [contactChanged, hasVerifiedContactNumber],
  );

  const verifiedEmail = useMemo(
    () => currentProfile?.hasVerifiedEmail && !emailChanged,
    [currentProfile?.hasVerifiedEmail, emailChanged],
  );

  const disabledVerification = useMemo(
    () => !phone?.phoneNumber || contactError,
    [contactError, phone],
  );

  const hasVerificationMobile = useMemo(() => !isSuperAdmin, [isSuperAdmin]);

  const isTablet = useMatchMedia('isTablet');

  const { t } = useTranslation();

  const commonValidations = useValidations();

  const uploadFile = useCallback(
    async (file, fileName, config) => {
      const data = await api.users.uploadFile(
        currentProfile?._id,
        file,
        fileName,
        config,
      );
      updateProfilePicture(data);
      return data;
    },
    [currentProfile?._id, updateProfilePicture],
  );

  const deleteFiles = useCallback(
    async (file, fileName) => {
      const {
        data: { fileId },
      } = await api.users.deleteFile(currentProfile?._id, fileName, file.id);
      updateProfilePicture(null);
      return fileId;
    },
    [currentProfile?._id, updateProfilePicture],
  );

  const profileImageField = useMemo(
    () => (
      <Field
        label={t('profileImage')}
        name="profileImage"
        fileName=""
        className="profile-image"
        component={DropzoneField}
        uploadRequest={uploadFile}
        deleteRequest={deleteFiles}
        maxFiles={1}
        accept="image/*"
        onDeleteMessage={{
          title: t('deleteProfileImage'),
          content: t('deleteProfileImageDescription'),
        }}
      />
    ),
    [deleteFiles, t, uploadFile],
  );

  return (
    <div className="profile-page__wrapper">
      <div className={classes}>
        <PhoneVerificationModal
          ref={phoneVerificationModalRef}
          phone={phone}
          disabled={disabledVerification}
        />

        <div className="profile-page__profile-details profile-card-container">
          <p className="profile-section-title">{t('contactInformation')}</p>
          <div className="form-row">
            <Field
              name="firstName"
              label={t('firstName')}
              component={InputField}
              required
              validate={commonValidations.name}
            />
            <Field
              name="lastName"
              label={t('lastName')}
              component={InputField}
              required
              validate={commonValidations.name}
            />
          </div>
          <div className="form-row">
            <Field
              name="title"
              label={t('title')}
              placeholder={t('businessBrokerAndManager')}
              component={InputField}
              validate={validations.noLongerThan(
                NAME_MAX_LENGTH,
                t('noLongerThan', { length: NAME_MAX_LENGTH }),
              )}
            />
            <EmailField required isVerified={verifiedEmail} />
          </div>
          <div className="form-row">
            <PhoneFieldWrapper
              verified={verifiedPhone}
              {...(hasVerificationMobile && {
                onVerification: phoneVerificationModal?.open,
              })}
            />
            <Field
              name="linkedinAccount"
              label={t('linkedinAccount')}
              component={InputField}
              validate={validations.linkedinValidation()}
            />
          </div>

          <ProfileActionsWithVerification />

          {isTablet && profileImageField}
        </div>

        <TemporaryNotifications />

        <Company>
          <ProfileActions />
        </Company>

        <div className="profile-card-container">
          <InvestorVerify />
        </div>
      </div>
      {!isTablet && profileImageField}
    </div>
  );
};

export default ProfilePage;
